import { useQuery } from '@tanstack/react-query';
import { api, type MailRecipientOption } from './api';
import type { TranslationKey } from './i18n';

/**
 * Calls (2026-10-29): voice calls between employees, one to one or in a group,
 * every connected one recorded — one mixed file of the whole call, kept in our
 * private bucket. The API decides everything a call is; LiveKit carries the audio.
 * Anyone really connected to a call plays its whole recording; an administrator
 * (`calls.view_all`) any recording.
 */

export type CallType = 'ONE_TO_ONE' | 'GROUP';
export type CallStatus =
  | 'RINGING'
  | 'PREPARING'
  | 'ACTIVE'
  | 'ENDING'
  | 'COMPLETED'
  | 'MISSED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'BUSY'
  | 'FAILED';
export type ParticipantStatus = 'RINGING' | 'ACCEPTED' | 'DECLINED' | 'MISSED' | 'BUSY' | 'CANCELLED' | 'LEFT';
/** What a recording shows as: still being made, being checked, ready, incomplete, failed, expired, or none. */
export type RecordingShown = 'NONE' | 'RECORDING' | 'PROCESSING' | 'READY' | 'PARTIAL' | 'FAILED' | 'EXPIRED';
/** How a call went for the reader. */
export type CallOutcome = 'LIVE' | 'ANSWERED' | 'NO_ANSWER' | 'MISSED' | 'DECLINED' | 'BUSY' | 'CANCELLED' | 'FAILED';
export type PlaybackRefusal = 'NOT_A_PARTICIPANT' | 'NOT_JOINED' | 'NO_RECORDING' | 'NOT_READY' | 'INCOMPLETE' | 'EXPIRED' | 'FAILED';

export interface CallPerson {
  id: string;
  fullName: string;
  fullNameAr: string | null;
  jobTitle: string | null;
  /**
   * One of our employees, or a client's portal sign-in (2026-11-01) — whose name says who and which company,
   * "Noura (Saudi Tech Co.)". Missing from an older API: one of ours.
   */
  kind?: 'EMPLOYEE' | 'CLIENT';
  /** A client's sign-in: its company. */
  client?: { id: string; name: string; nameAr: string | null } | null;
}

/** A client's portal sign-in on a call (2026-11-01). */
export const isClientParty = (p: Pick<CallPerson, 'kind'> | null | undefined) => p?.kind === 'CLIENT';

/** How someone on a call shows in its room: `user_<id>`, or `client_<id>` for a client's sign-in (domain/calls.ts). */
export const roomIdentity = (p: Pick<CallPerson, 'id' | 'kind'>) => (isClientParty(p) ? `client_${p.id}` : `user_${p.id}`);

export interface CallParticipant {
  user: CallPerson;
  isCaller: boolean;
  status: ParticipantStatus;
  invitedAt: string;
  answeredAt: string | null;
  /** The server saw them in the call's room. */
  joinedAt: string | null;
  leftAt: string | null;
  inRoom: boolean;
  leftReason: string | null;
}

/** `current` of `GET /calls/live`: my call now. */
export interface LiveCall {
  id: string;
  type: CallType;
  status: CallStatus;
  createdAt: string;
  startedAt: string | null;
  endsBy: string | null;
  endingSoon: boolean;
  caller: CallPerson | null;
  participants: CallParticipant[];
  recorded: true;
  recording: RecordingShown;
  failureCode: string | null;
  me: {
    status: ParticipantStatus;
    isCaller: boolean;
    /** This tab is the one that answered, or called. Only it joins the room. */
    thisDevice: boolean;
    /** The recorder is confirmed: the microphone may go on. */
    maySpeak: boolean;
    mayAddPeople: boolean;
  };
}

export interface IncomingCall {
  callId: string;
  type: CallType;
  caller: CallPerson | null;
  invitedBy: CallPerson | null;
  people: number;
  /** A call already going on that I am asked into. */
  underway: boolean;
  ringUntil: string;
  recorded: true;
}

export interface LiveState {
  serverTime: string;
  incoming: IncomingCall[];
  current: LiveCall | null;
}

export interface CallSummary {
  id: string;
  type: CallType;
  status: CallStatus;
  /** Between employees, or a client's call from the portal (2026-11-01). Missing from an older API. */
  kind?: 'INTERNAL' | 'CLIENT';
  /** A client's call: the client. */
  client?: { id: string; name: string; nameAr: string | null } | null;
  outcome: CallOutcome;
  direction: 'OUTGOING' | 'INCOMING' | null;
  createdAt: string;
  startedAt: string | null;
  endedAt: string | null;
  durationSeconds: number | null;
  caller: CallPerson | null;
  people: Array<CallPerson & { joined: boolean; status: ParticipantStatus }>;
  recording: { shown: RecordingShown; playable: boolean; durationSeconds: number | null };
  /** Its AI summary is written and the reader may read it (lib/call-ai.ts). Missing from an older API. */
  ai?: { ready: boolean };
}

export interface CallDetail extends Omit<CallSummary, 'recording'> {
  endedReason: string | null;
  failureCode: string | null;
  participants: CallParticipant[];
  recording: {
    shown: RecordingShown;
    playable: boolean;
    refusal: PlaybackRefusal | null;
    durationSeconds: number | null;
    sizeBytes: number | null;
    readyAt: string | null;
    deletedAt: string | null;
    expiresAt: string | null;
    failureReason: string | null;
  };
  events: Array<{ type: string; at: string; user: CallPerson | null }>;
  /** Calls › AI summary: whether this call is ever summarized (fixed when it was made), and why the reader may not read it. */
  aiSummary?: { eligible: boolean; refusal: 'NOT_A_PARTICIPANT' | 'NOT_JOINED' | null };
  can: { play: boolean; readAccessLog: boolean; readAiSummary?: boolean };
}

/** A screen shared on a call, from its history (2026-11-02): who, from when, until when — null while not said. */
export interface ScreenShareSpan {
  user: CallPerson | null;
  from: string;
  until: string | null;
}

/** The call's history, read for its shared screens: each one begun, and when its sharer stopped it. */
export function screenShares(events: CallDetail['events']): ScreenShareSpan[] {
  const spans: ScreenShareSpan[] = [];
  for (const e of events) {
    if (e.type === 'screen.shared') spans.push({ user: e.user, from: e.at, until: null });
    else if (e.type === 'screen.stopped') {
      const open = spans.find((s) => s.until === null && s.user?.id === e.user?.id);
      if (open) open.until = e.at;
    }
  }
  return spans;
}

export interface CallPage {
  items: CallSummary[];
  nextCursor: string | null;
}

export interface CallsStatus {
  available: boolean;
  reasons: string[];
  /** What is missing on the server, by its `.env` name — for whoever runs the system. */
  missing: string[];
  settings: CallSettings;
}

export interface CallSettings {
  recordingRetentionDays: number | null;
  ringTimeoutSeconds: number;
  maxParticipants: number;
  maxDurationMinutes: number;
}

export interface PlaybackLink {
  url: string;
  expiresAt: string;
  mimeType: string;
  durationSeconds: number | null;
  partial: boolean;
}

export interface AccessRow {
  id: string;
  action: 'PLAYBACK_AUTHORIZED' | 'PLAYBACK_DENIED';
  reason: PlaybackRefusal | null;
  channel: 'WEB' | 'MOBILE' | null;
  ipAddress: string | null;
  createdAt: string;
  actor: { id: string; fullName: string; fullNameAr: string | null } | null;
}

/** The choices Settings › Calls offers besides Keep forever and a number of days of one's own. */
export const RETENTION_PRESETS = [30, 180, 365] as const;
export const MAX_RETENTION_DAYS = 36_500;

// ---------------------------------------------------------------------------
// This tab
// ---------------------------------------------------------------------------

const DEVICE_KEY = 'viganium.callDevice';

/**
 * This tab's own name for the calls it answers: one per tab, kept while the tab
 * lives (a reload keeps it), so an answer here is this tab's alone and another
 * tab of the same person shows "answered elsewhere".
 */
export function callDeviceId(): string {
  try {
    const kept = sessionStorage.getItem(DEVICE_KEY);
    if (kept && /^[A-Za-z0-9_-]{8,64}$/.test(kept)) return kept;
    const fresh = `web-${crypto.randomUUID()}`;
    sessionStorage.setItem(DEVICE_KEY, fresh);
    return fresh;
  } catch {
    return `web-${crypto.randomUUID()}`;
  }
}

// ---------------------------------------------------------------------------
// The API
// ---------------------------------------------------------------------------

export const callsApi = {
  status: () => api.get<CallsStatus>('/calls/status'),
  people: () => api.get<MailRecipientOption[]>('/calls/people'),
  live: (deviceId: string) => api.get<LiveState>('/calls/live', { deviceId }),
  create: (userIds: string[], deviceId: string) => api.post<LiveCall>('/calls', { userIds, deviceId }),
  invite: (callId: string, userIds: string[]) => api.post<LiveCall>(`/calls/${callId}/invitations`, { userIds }),
  accept: (callId: string, deviceId: string) => api.post<LiveCall>(`/calls/${callId}/accept`, { deviceId }),
  decline: (callId: string) => api.post(`/calls/${callId}/decline`),
  hangUp: (callId: string) => api.post(`/calls/${callId}/hangup`),
  mediaToken: (callId: string, deviceId: string) =>
    api.post<{ url: string; token: string; identity: string; maySpeak: boolean }>(`/calls/${callId}/media-token`, { deviceId }),
  detail: (callId: string) => api.get<CallDetail>(`/calls/${callId}`),
  playback: (callId: string) => api.post<PlaybackLink>(`/calls/${callId}/recording/playback`),
  access: (callId: string) => api.get<AccessRow[]>(`/calls/${callId}/recording/access`),
  settings: () => api.get<CallSettings>('/calls/settings'),
  retentionPreview: (days: number | null) =>
    api.get<{ days: number | null; affected: number }>('/calls/settings/retention-preview', days === null ? {} : { days }),
  saveSettings: (values: CallSettings & { confirmDeletion?: boolean }) => api.put<CallSettings>('/calls/settings', values),
};

/** Whether calls can be made here, and the settings a call follows. Read once a minute. */
export function useCallsStatus() {
  return useQuery({ queryKey: ['calls', 'status'], queryFn: callsApi.status, staleTime: 60_000, refetchInterval: 5 * 60_000 });
}

/** Everyone a call can be made to. */
export function useCallPeople(enabled = true) {
  return useQuery({ queryKey: ['calls', 'people'], queryFn: callsApi.people, staleTime: 5 * 60_000, enabled });
}

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------

export const OUTCOME_LABEL: Record<CallOutcome, TranslationKey> = {
  LIVE: 'calls.outcome.LIVE',
  ANSWERED: 'calls.outcome.ANSWERED',
  NO_ANSWER: 'calls.outcome.NO_ANSWER',
  MISSED: 'calls.outcome.MISSED',
  DECLINED: 'calls.outcome.DECLINED',
  BUSY: 'calls.outcome.BUSY',
  CANCELLED: 'calls.outcome.CANCELLED',
  FAILED: 'calls.outcome.FAILED',
};

export const OUTCOME_TONE: Record<CallOutcome, 'success' | 'error' | 'warning' | 'neutral' | 'info'> = {
  LIVE: 'info',
  ANSWERED: 'success',
  NO_ANSWER: 'warning',
  MISSED: 'error',
  DECLINED: 'neutral',
  BUSY: 'warning',
  CANCELLED: 'neutral',
  FAILED: 'error',
};

export const RECORDING_LABEL: Record<RecordingShown, TranslationKey> = {
  NONE: 'calls.recording.NONE',
  RECORDING: 'calls.recording.RECORDING',
  PROCESSING: 'calls.recording.PROCESSING',
  READY: 'calls.recording.READY',
  PARTIAL: 'calls.recording.PARTIAL',
  FAILED: 'calls.recording.FAILED',
  EXPIRED: 'calls.recording.EXPIRED',
};

export const RECORDING_TONE: Record<RecordingShown, 'success' | 'error' | 'warning' | 'neutral' | 'info'> = {
  NONE: 'neutral',
  RECORDING: 'error',
  PROCESSING: 'info',
  READY: 'success',
  PARTIAL: 'warning',
  FAILED: 'error',
  EXPIRED: 'neutral',
};

export const PARTICIPANT_LABEL: Record<ParticipantStatus, TranslationKey> = {
  RINGING: 'calls.participant.RINGING',
  ACCEPTED: 'calls.participant.ACCEPTED',
  DECLINED: 'calls.participant.DECLINED',
  MISSED: 'calls.participant.MISSED',
  BUSY: 'calls.participant.BUSY',
  CANCELLED: 'calls.participant.CANCELLED',
  LEFT: 'calls.participant.LEFT',
};

/** Why a call stopped, said plainly — never a provider's words. */
export function endedText(code: string | null | undefined): TranslationKey {
  switch (code) {
    case 'RECORDING_NOT_READY':
    case 'RECORDING_NOT_STARTED':
    case 'RECORDING_STORAGE_UNREACHABLE':
      return 'calls.ended.recordingNotStarted';
    case 'RECORDING_LOST':
    case 'RECORDING_UNCONFIRMED':
      return 'calls.ended.recordingLost';
    case 'MAX_DURATION':
      return 'calls.ended.maxDuration';
    default:
      return 'calls.ended.normal';
  }
}

/** Between names in a list: the Arabic comma in Arabic. */
export function listSeparator(locale: 'en' | 'ar'): string {
  return locale === 'ar' ? '، ' : ', ';
}

/** `m:ss`, or `h:mm:ss` past an hour. */
export function callClock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

// ---------------------------------------------------------------------------
// The ring
// ---------------------------------------------------------------------------

/**
 * The ring of a call coming in (operator, 2026-10-29: "when call comes there is no tone must have to tone to
 * know i have incoming calls"): a telephone's warble — C6 and E6 in turn, 20 times a second — in two bursts of
 * 0.4 s, 0.2 s apart, every 3 s. Pitched where a laptop's speakers are clear, and loud: the 440/480 Hz blips it
 * replaces sounded some 20 ms each, and went unheard. Nothing like the ringback (425 Hz) or the bell's chime.
 */
export const RING = { low: 1046.5, high: 1318.5, trillMs: 25, burstMs: 400, gapMs: 200, everyMs: 3000, volume: 0.25 } as const;

/** What lets a browser sound a page: a click, a key — a tap once the finger lifts. */
const WAKE_EVENTS = ['pointerdown', 'pointerup', 'keydown'] as const;

let ringContext: AudioContext | null = null;
let ringOn = false;
let ringTimer = 0;
/** Where this ring goes: let go of when it stops, so nothing of it sounds after — a burst half played included. */
let ringOut: GainNode | null = null;

/** The ring's two bursts, from `start` on `ctx`'s clock, into `out`. */
export function drawRing(ctx: BaseAudioContext, out: AudioNode, start: number): void {
  for (const at of [0, (RING.burstMs + RING.gapMs) / 1000]) {
    const from = start + at;
    const to = from + RING.burstMs / 1000;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = RING.low;
    // The trill: the pitch moves with the wave, so the notes change without a click.
    for (let n = 0; n < RING.burstMs / RING.trillMs; n++) {
      osc.frequency.setValueAtTime(n % 2 ? RING.high : RING.low, from + (n * RING.trillMs) / 1000);
    }
    // Faded in and out, so it never clicks.
    gain.gain.setValueAtTime(0.0001, from);
    gain.gain.exponentialRampToValueAtTime(RING.volume, from + 0.01);
    gain.gain.setValueAtTime(RING.volume, to - 0.03);
    gain.gain.exponentialRampToValueAtTime(0.0001, to);
    osc.connect(gain).connect(out);
    osc.start(from);
    osc.stop(to + 0.01);
  }
}

function ringAudio(): AudioContext | null {
  if (!ringContext) {
    try {
      ringContext = new AudioContext();
    } catch {
      return null;
    }
    // A context the browser held back rings the moment it is let.
    ringContext.addEventListener('statechange', ringNow);
  }
  return ringContext;
}

/** The ring sounds once its context runs — at once, or when the browser lets it — and every 3 s from then. */
function ringNow(): void {
  if (!ringOn || ringTimer || ringContext?.state !== 'running') return;
  ringBurst();
  ringTimer = window.setInterval(ringBurst, RING.everyMs);
}

/** Never drawn while the context stands still: the bursts would pile up, and all sound at once when it ran. */
function ringBurst(): void {
  const ctx = ringContext;
  if (ctx?.state === 'running' && ringOut) drawRing(ctx, ringOut, ctx.currentTime + 0.02);
}

/** A click or a key while it rings: the browser lets the page sound now (Firefox and Safari wait for one). */
function wakeRing(): void {
  if (ringOn && ringContext && ringContext.state !== 'running') void ringContext.resume().catch(() => undefined);
}

/**
 * Rings while a call rings for this person. It is always tried, though nobody has clicked in the page since it
 * loaded: a site the person lets play sound — Chrome's site setting Sound › Allow, Firefox's Autoplay › Allow
 * Audio and Video — sounds without a click, after a reload too. Otherwise the browser holds the page silent until
 * its first click or key press — F5 starts that over — and the ring sounds from then, while the call still rings;
 * until then the card on the page and the desktop notification (Web Push) show the call.
 */
export function startRinging(): void {
  if (ringOn || typeof AudioContext === 'undefined') return;
  const ctx = ringAudio();
  if (!ctx) return;
  ringOn = true;
  ringOut = ctx.createGain();
  ringOut.connect(ctx.destination);
  for (const type of WAKE_EVENTS) window.addEventListener(type, wakeRing, true);
  // Asked every time — after the last ring's suspend too, which a browser may still be carrying out.
  void ctx.resume().catch(() => undefined);
  ringNow();
}

/** Answered, declined, missed or called off: the ring stops at once — a burst half played included. */
export function stopRinging(): void {
  if (!ringOn) return;
  ringOn = false;
  window.clearInterval(ringTimer);
  ringTimer = 0;
  for (const type of WAKE_EVENTS) window.removeEventListener(type, wakeRing, true);
  ringOut?.disconnect();
  ringOut = null;
  void ringContext?.suspend().catch(() => undefined);
}

let ringbackContext: AudioContext | null = null;
let ringbackTimer = 0;

/** The ringback tone: 425 Hz, a second on and four off — what a phone network plays a caller here. */
export const RINGBACK = { hz: 425, onMs: 1000, everyMs: 5000 } as const;

/**
 * What the caller hears while the call rings for the others (operator, 2026-10-29: "when i'm calling
 * someone i need voice to know i'm calling"): the ringback tone, drawn with Web Audio, quiet, until
 * someone answers or the call is called off. Its own audio context, so stopping it never cuts the ring
 * of a call coming in, nor the other way round.
 */
export function startRingback(): void {
  if (ringbackTimer) return;
  if (typeof AudioContext === 'undefined') return;
  if (navigator.userActivation && !navigator.userActivation.hasBeenActive) return;
  ringbackContext ??= new AudioContext();
  const ctx = ringbackContext;
  const tone = () => {
    if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
    const start = ctx.currentTime + 0.02;
    const end = start + RINGBACK.onMs / 1000;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = RINGBACK.hz;
    // Faded in and out, so it never clicks.
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.1, start + 0.04);
    gain.gain.setValueAtTime(0.1, end - 0.04);
    gain.gain.exponentialRampToValueAtTime(0.0001, end);
    osc.connect(gain).connect(ctx.destination);
    osc.start(start);
    osc.stop(end + 0.01);
  };
  tone();
  ringbackTimer = window.setInterval(tone, RINGBACK.everyMs);
}

/** Answered, declined, missed or called off: the ringback stops at once — a tone half played included. */
export function stopRingback(): void {
  if (!ringbackTimer) return;
  window.clearInterval(ringbackTimer);
  ringbackTimer = 0;
  void ringbackContext?.suspend().catch(() => undefined);
}

/**
 * Asks for the microphone inside the click that calls or answers, so the
 * browser's question comes then — not in the middle of the call, when the
 * recorder is confirmed and the microphone is turned on. The stream is let go at
 * once: nothing is sent before the call is live.
 *
 * `blocked`: the browser refuses it (the person, or a block left on the site —
 * Firefox keeps one for the tab without asking again); `missing`: none could be
 * opened (none there, or held by another program).
 */
export async function askForMicrophone(): Promise<'granted' | 'blocked' | 'missing'> {
  if (!navigator.mediaDevices?.getUserMedia) return 'missing';
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    for (const track of stream.getTracks()) track.stop();
    return 'granted';
  } catch (e) {
    const name = (e as DOMException)?.name;
    return name === 'NotAllowedError' || name === 'SecurityError' ? 'blocked' : 'missing';
  }
}

/**
 * A call not made, or not answered, because this browser will not give the
 * microphone: a voice call nobody can be heard in helps nobody, and the person
 * learns why at once instead of in silence.
 */
export class MicrophoneRefused extends Error {
  constructor(readonly reason: 'blocked' | 'missing') {
    super(`The microphone is ${reason}.`);
    this.name = 'MicrophoneRefused';
  }
}
